import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminContactInfoQuery } from '@/administration/administration.hooks';
import { useAdminContactInfo } from '@/administration/administration.store';
import { openAIAssistantWithExternalPrompt } from '@/aiAssistant/aiAssistant.actions';
import { sqAiAssistantStore, sqHomeScreenStore, sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { SimpleSaveFormBuilder } from '@/formbuilder/SimpleSaveFormBuilder.page';
import { CopyBranchCode } from '@/homescreen/CopyBranchCode.atom';
import { setTrainingPromptDisplay } from '@/homescreen/homescreen.actions';
import { TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import { LoadingFallback } from '@/main/LoadingFallback.atom';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import { onHomeScreen } from '@/main/routing.utilities';
import { extractError } from '@/requests/axios.utilities';
import { isTrustedUser } from '@/services/authorization.service';
import { isLearnModeEnabled, isNewHomeScreen2025Enabled } from '@/services/systemConfiguration.utilities';
import { doTrack } from '@/track/track.service';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { toBase64 } from '@/utilities/utilities';
import { sqLogsApi } from '@seeqdev/api/endpoint/LogsApi';
import { FrequencyEnum, SeverityEnum } from '@seeqdev/api/model/SupportRequestInputV1';
import { Alert, Icon, Modal } from '@seeqdev/qomponents';
import type { FormElement } from '@/formbuilder/formBuilder.constants';
import type { AttachmentInputV1 } from '@seeqdev/api/model/AttachmentInputV1';
import type { SupportRequestInputV1 } from '@seeqdev/api/model/SupportRequestInputV1';

enum ReasonOption {
  Problem = 'Problem',
  Feature = 'Feature',
  Other = 'Other',
}

interface SupportRequestDetails {
  reason: keyof typeof ReasonOption;
  summary: string;
  description: string;
}

interface HelpCenterModalProps {
  onClose: () => void;
  defaultActiveKey?: '0' | '1';
  details?: Partial<SupportRequestDetails>;
}

interface ActionCardProps {
  icon: string;
  title: string;
  description: string;
  actionText: string;
  onClick: () => void;
  testId: string;
  display?: boolean;
  children?: React.ReactNode;
}

const ActionCard = ({
  icon,
  title,
  description,
  actionText,
  onClick,
  testId,
  display = true,
  children,
}: ActionCardProps) => {
  if (!display) {
    return null;
  }

  return (
    <div
      className="roundedBorder lightGreyBorder flexRowContainer flexSpaceBetween p16 cursorPointer max-width-250"
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      data-testid={testId}>
      <div className="flexColumnContainer flexAlignCenter gap8 pb12">
        <Icon icon={icon} extraClassNames="font-size-large" type="theme" />
        <h4 className="m0">{title}</h4>
      </div>
      <div className={`flexRowContainer flexGrow ${children ? 'pb4' : 'pb12'}`}>
        <p className="m0">{description}</p>
        {children && <div className="font-size-smaller">{children}</div>}
      </div>
      <div className="flexColumnContainer flexAlignCenter gap6 semi-bold flexAlignBottom">
        <span className="link">{actionText}</span>
        <Icon icon="fa-arrow-right" small={true} type="theme" />
      </div>
    </div>
  );
};

const HelpCenterModalContent = ({ onClose, defaultActiveKey = '0', details = {} }: HelpCenterModalProps) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const [name, setName] = useState(
    `${sqWorkbenchStore.currentUser.firstName ?? ''} ${sqWorkbenchStore.currentUser.lastName ?? ''}`.trim(),
  );
  const [email, setEmail] = useState(sqWorkbenchStore.currentUser.email);
  const [summary, setSummary] = useState(details.summary ?? '');
  const [description, setDescription] = useState(details.description ?? '');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [reason, setReason] = useState(details.reason ? ReasonOption[details.reason] : undefined);
  const [frequency, setFrequency] = useState<FrequencyEnum>(FrequencyEnum.NotApplicable);
  const [severity, setSeverity] = useState<SeverityEnum>(SeverityEnum.S4);
  const [file, setFile] = useState<File>();
  const [contactUsExpanded, setContactUsExpanded] = useState(defaultActiveKey === '1');

  const adminContactInfo = useAdminContactInfo();
  const isAiAssistantDisplayed = useFluxPath(sqAiAssistantStore, () => sqAiAssistantStore.displayed);
  const branchCode = useFluxPath(sqHomeScreenStore, () => sqHomeScreenStore.branchCode) || '';

  const closeModal = () => {
    onClose();
  };

  const openApiReference = () => {
    doTrack('Help Center', 'API Reference', 'open');
    const swaggerUrl = `/api-docs/?baseUrl=${window.encodeURIComponent(`${APPSERVER_API_PREFIX}/swagger.json`)}`;
    window.open(swaggerUrl);
  };

  const openWelcomeGuide = () => {
    doTrack('Help Center', 'Welcome Guide', 'open');
    const isNewHomeScreen = isNewHomeScreen2025Enabled();

    if (isNewHomeScreen) {
      setTrainingPromptDisplay(TrainingPromptDisplay.HOME_SCREEN);

      const isOnHomeScreen = onHomeScreen();
      if (isOnHomeScreen) {
        closeModal();
      } else {
        window.open('/', '_blank');
      }
    } else {
      setTrainingPromptDisplay(TrainingPromptDisplay.MODAL);
      closeModal();
    }
  };

  const openSkillExplorer = () => {
    doTrack('Help Center', 'Skill Explorer', 'open');
    window.open('https://skill-explorer.seeq.com/');
  };

  const openTraining = () => {
    doTrack('Help Center', 'Training', 'open');
    window.open('https://training.seeq.com');
  };

  const openKnowledgeBase = () => {
    doTrack('Help Center', 'Knowledge Base', 'open');
    window.open('https://support.seeq.com/');
  };

  const openAIAssistant = () => {
    doTrack('Help Center', 'AI Assistant', 'open');
    openAIAssistantWithExternalPrompt(
      { prompt: t('AI_ASSISTANT.HELP_PROMPT'), agent: 'general' },
      { wasDisplayed: isAiAssistantDisplayed },
    );
    closeModal();
  };

  const sendSupportRequest = async () => {
    let attachment: AttachmentInputV1 | undefined;
    if (file) {
      const content = await toBase64(file);
      attachment = { content, filename: file.name, type: file.type };
    }

    setErrorMessage('');
    const supportRequest: SupportRequestInputV1 = {
      name,
      email,
      summary,
      description,
      frequency,
      severity,
      stepsToReproduce,
      attachment,
    };
    try {
      const response = await sqLogsApi.send(supportRequest);
      successToast({
        messageKey: 'HELP_CENTER.SENT',
        buttonLabelKey: 'HELP_CENTER.VIEW_REQUEST',
        buttonAction: () => window.open(response.data.issueUrl),
        buttonIcon: 'fa-arrow-up-right-from-square',
      });
      closeModal();
    } catch (e) {
      setErrorMessage(extractError(e).message);
    }
  };

  const reasonOptions = [
    { label: 'HELP_CENTER.REASON_OPTIONS.PROBLEM', value: ReasonOption.Problem },
    { label: 'HELP_CENTER.REASON_OPTIONS.FEATURE', value: ReasonOption.Feature },
    { label: 'HELP_CENTER.REASON_OPTIONS.OTHER', value: ReasonOption.Other },
  ];
  const descriptionPlaceholders = {
    [ReasonOption.Problem]: 'HELP_CENTER.OBSERVED_PLACEHOLDER',
    [ReasonOption.Feature]: 'HELP_CENTER.FEATURE_PLACEHOLDER',
    [ReasonOption.Other]: 'HELP_CENTER.DESCRIPTION_PLACEHOLDER',
  };
  const frequencyOptions = [
    { label: t('HELP_CENTER.FREQUENCY_OPTIONS.NA'), value: FrequencyEnum.NotApplicable },
    { label: t('HELP_CENTER.FREQUENCY_OPTIONS.ONCE'), value: FrequencyEnum.Once },
    { label: t('HELP_CENTER.FREQUENCY_OPTIONS.INFREQUENTLY'), value: FrequencyEnum.Infrequently },
    { label: t('HELP_CENTER.FREQUENCY_OPTIONS.EVERY_TIME'), value: FrequencyEnum.EveryTime },
  ];
  const severityOptions = [
    { label: t('HELP_CENTER.SEVERITY_OPTIONS.S4'), value: SeverityEnum.S4 },
    { label: t('HELP_CENTER.SEVERITY_OPTIONS.S3'), value: SeverityEnum.S3 },
    { label: t('HELP_CENTER.SEVERITY_OPTIONS.S2'), value: SeverityEnum.S2 },
    { label: t('HELP_CENTER.SEVERITY_OPTIONS.S1'), value: SeverityEnum.S1 },
  ];

  const isReasonSelected = !_.isUndefined(reason);
  const formDefinition: FormElement[] = [
    {
      component: 'FormRow',
      name: 'nameAndEmailRow',
      extraClassNames: 'flexSpaceBetween',
      components: [
        {
          component: 'TextFieldFormComponent',
          name: 'name',
          label: 'USER.NAME',
          value: name,
          required: true,
          onChange: (value) => setName(value as string),
          size: 'sm',
          testId: 'name',
          wrapperClassNames: 'flexBasis100Percent',
        },
        {
          component: 'TextFieldFormComponent',
          name: 'email',
          label: 'USER.EMAIL',
          value: email,
          required: true,
          onChange: (value) => setEmail(value as string),
          size: 'sm',
          testId: 'email',
          wrapperClassNames: 'flexBasis100Percent ml10',
        },
      ],
    },
    {
      component: 'TextFieldFormComponent',
      name: 'summary',
      label: 'HELP_CENTER.SUMMARY',
      placeholder: 'HELP_CENTER.SUMMARY_PLACEHOLDER',
      value: summary,
      required: true,
      onChange: (value) => setSummary(value as string),
      size: 'sm',
      testId: 'requestSummary',
    },
    {
      component: 'SelectFormComponent',
      name: 'selectReason',
      label: 'HELP_CENTER.REASON',
      value: reason?.toString() || '',
      onChange: (value) => setReason(ReasonOption[value as keyof typeof ReasonOption]),
      options: reasonOptions,
      required: true,
      insideModal: true,
      testId: 'selectReason',
    },
    {
      component: 'SelectFormComponent',
      includeIf: reason === ReasonOption.Problem,
      name: 'selectSeverity',
      label: 'HELP_CENTER.SEVERITY',
      value: severity.toString(),
      onChange: (value) => setSeverity(SeverityEnum[value as keyof typeof SeverityEnum]),
      options: severityOptions,
      required: true,
      insideModal: true,
    },
    {
      component: 'FormRow',
      includeIf: isReasonSelected,
      name: 'frequencyAndTimeRow',
      extraClassNames: 'flexSpaceBetween mb8',
      components: [
        {
          component: 'SelectFormComponent',
          includeIf: reason === ReasonOption.Problem,
          name: 'selectFrequency',
          label: 'HELP_CENTER.FREQUENCY',
          value: frequency.toString(),
          onChange: (value) => setFrequency(FrequencyEnum[value as keyof typeof FrequencyEnum]),
          options: frequencyOptions,
          required: true,
          insideModal: true,
          wrapperClassNames: 'flexBasis100Percent mr10',
        },
        {
          component: 'FileDropperFormComponent',
          value: file?.name,
          filename: file?.name,
          name: 'fileUploadInput',
          id: 'fileUploadInput',
          text: 'HELP_CENTER.SCREENSHOT',
          actionText: 'HELP_CENTER.SCREENSHOT_ATTACHED',
          fileExtension: '.png,.jpg,.gif,.webp',
          fileDropCallback: async (file) => setFile(file),
          clearFileCallback: () => {
            setFile(undefined);
          },
          extraClassNames: 'flexBasis100Percent',
          validation: () => false,
        },
      ],
    },
    {
      component: 'TextFieldFormComponent',
      includeIf: isReasonSelected,
      name: 'description',
      label: reason === ReasonOption.Problem ? 'HELP_CENTER.OBSERVED' : 'HELP_CENTER.DESCRIPTION',
      placeholder: isReasonSelected ? descriptionPlaceholders[reason] : '',
      value: description,
      required: true,
      onChange: (value) => setDescription(value as string),
      size: 'sm',
      testId: 'requestDescription',
      as: 'textarea',
      rows: 2,
    },
    {
      component: 'TextFieldFormComponent',
      includeIf: reason === ReasonOption.Problem,
      name: 'stepsToReproduce',
      label: 'HELP_CENTER.STEPS_TO_REPRODUCE',
      placeholder: 'HELP_CENTER.STEPS_TO_REPRODUCE_PLACEHOLDER',
      value: stepsToReproduce,
      required: false,
      onChange: (value) => setStepsToReproduce(value as string),
      size: 'sm',
      testId: 'requestStepsToReproduce',
      as: 'textarea',
      rows: 2,
    },
  ];

  return (
    <Modal
      open={true}
      onClose={closeModal}
      onPointerDownOutside={closeModal}
      testId="helpCenterModal"
      title={t('HELP_CENTER.TITLE')}
      hideFooterButtons={true}>
      <div className="p24">
        {/* Action Cards Grid */}
        <div className="helpCenterGrid mb20">
          <ActionCard
            icon="fc-external-link"
            title={t('HELP_CENTER.WELCOME_CARD_TITLE')}
            description={t('HELP_CENTER.WELCOME_CARD_DESC')}
            actionText={t('HELP_CENTER.WELCOME_CARD_ACTION_TEXT')}
            onClick={openWelcomeGuide}
            testId="welcomeCard"
          />
          <ActionCard
            icon="fa-sparkles"
            title={t('HELP_CENTER.AI_CARD_TITLE')}
            description={t('HELP_CENTER.AI_CARD_DESC')}
            actionText={t('HELP_CENTER.AI_CARD_ACTION_TEXT')}
            onClick={openAIAssistant}
            testId="aiAssistantCard"
            display={isTrustedUser()}
          />
          <ActionCard
            icon="fc-training"
            title={t('HELP_CENTER.TRAINING_CARD_TITLE')}
            description={t('HELP_CENTER.TRAINING_CARD_DESC')}
            actionText={t('HELP_CENTER.TRAINING_CARD_ACTION_TEXT')}
            onClick={openTraining}
            testId="trainingCard"
            display={isTrustedUser()}>
            {!isLearnModeEnabled() &&
              (branchCode ? (
                <CopyBranchCode text="HOME_SCREEN.TRAINING.BRANCH_CODE" branchCode={branchCode} />
              ) : (
                t('HOME_SCREEN.TRAINING.ASK_ADMIN_BRANCH_CODE')
              ))}
          </ActionCard>
          <ActionCard
            icon="fc-skill-explorer"
            title={t('HELP_CENTER.SKILL_EXPLORER_CARD_TITLE')}
            description={t('HELP_CENTER.SKILL_EXPLORER_CARD_DESC')}
            actionText={t('HELP_CENTER.SKILL_EXPLORER_CARD_ACTION_TEXT')}
            onClick={openSkillExplorer}
            testId="skillExplorerCard"
            display={isTrustedUser()}
          />
          <ActionCard
            icon="fa-book"
            title={t('HELP_CENTER.KB_CARD_TITLE')}
            description={t('HELP_CENTER.KB_CARD_DESC')}
            actionText={t('HELP_CENTER.KB_CARD_ACTION_TEXT')}
            onClick={openKnowledgeBase}
            testId="knowledgeBaseCard"
          />
          <ActionCard
            icon="fa-code"
            title={t('HELP_CENTER.API_CARD_TITLE')}
            description={t('HELP_CENTER.API_CARD_DESC')}
            actionText={t('HELP_CENTER.API_CARD_ACTION_TEXT')}
            onClick={openApiReference}
            testId="apiReferenceCard"
            display={isTrustedUser()}
          />
        </div>

        {/* Administrator Contact Card */}
        <div className="roundedBorder lightGreyBorder mb20 p16">
          <div className="flexColumnContainer flexAlignCenter gap8">
            <Icon icon="fa-user" extraClassNames="font-size-large" type="theme" />
            <h4 className="m0">
              {t('HELP_CENTER.ADMIN_CONTACT')}
              <a href={`mailto:${adminContactInfo.email}`} className="ml5">
                {adminContactInfo.name}
              </a>
            </h4>
          </div>
        </div>

        {/* Contact Form */}
        <div className="roundedBorder lightGreyBorder mb20 overflowHidden">
          <button
            type="button"
            className={`h4 width-maximum noBorder p16 mt0 mb0 flexColumnContainer flexAlignCenter flexSpaceBetween cursorPointer ${
              contactUsExpanded ? 'is-expanded' : ''
            }`}
            onClick={() => setContactUsExpanded((prev) => !prev)}
            aria-expanded={contactUsExpanded}
            aria-controls="support-request-contact-body">
            <span className="flexColumnContainer flexAlignCenter gap8">
              <Icon icon="fa-envelope" extraClassNames="font-size-large" type="theme" />
              {t('HELP_CENTER.CONTACT_US_HEADER')}
            </span>
            <Icon icon={contactUsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} type="text" />
          </button>
          {contactUsExpanded && (
            <div className="pt0 pl16 pr16 pb6" id="support-request-contact-body">
              {errorMessage && (
                <Alert extraClassNames="p10 mb16" variant="danger" dismissible={false}>
                  {errorMessage}
                  <a
                    href="https://seeq.atlassian.net/servicedesk/customer/portal/3"
                    target="_blank"
                    className="displayBlock mt8"
                    rel="noopener">
                    {t('HELP_CENTER.OPEN_PORTAL')}
                  </a>
                </Alert>
              )}
              <SimpleSaveFormBuilder
                formDefinition={formDefinition}
                submitFn={sendSupportRequest}
                closeFn={closeModal}
                submitBtnLabel="HELP_CENTER.SEND"
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export const HelpCenterModal = (props: HelpCenterModalProps) => {
  const adminContactInfo = useAdminContactInfo();
  const { isLoading, isError, error } = useAdminContactInfoQuery(!adminContactInfo.name || !adminContactInfo.email);

  if (isLoading) {
    return <LoadingFallback size="sm" />;
  }

  if (isError && error) {
    errorToast({ httpResponseOrError: error });
    throw error;
  }

  return <HelpCenterModalContent {...props} />;
};
